import { useMemo } from 'react'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js'
import { Line } from 'react-chartjs-2'
import type { TimelineDataPoint, Theme } from '@/types'
import { formatCurrency } from '@/utils/calculations'
import { getChartTheme, hexToRgba, getMutedColor } from '@/utils/chartConfig'

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler)

interface GrowthLineChartProps {
  timeline: TimelineDataPoint[]
  primaryColor?: string
  theme?: Theme
  showLegend?: boolean
  height?: number
}

export default function GrowthLineChart({
  timeline,
  primaryColor = '#14b8a6',
  theme = 'dark',
  showLegend = true,
  height = 300,
}: GrowthLineChartProps) {
  const chartTheme = useMemo(() => getChartTheme(theme), [theme])
  const mutedColor = useMemo(() => getMutedColor(primaryColor, theme), [primaryColor, theme])

  const data = useMemo(
    () => ({
      labels: timeline.map((p) => `Year ${p.year}`),
      datasets: [
        {
          label: 'With Advice',
          data: timeline.map((p) => p.withAdvice),
          borderColor: primaryColor,
          backgroundColor: hexToRgba(primaryColor, 0.1),
          fill: true,
          tension: 0.4,
          pointRadius: 0,
          pointHoverRadius: 6,
          borderWidth: 2,
        },
        {
          label: 'Without Advice',
          data: timeline.map((p) => p.withoutAdvice),
          borderColor: mutedColor,
          backgroundColor: hexToRgba(mutedColor, 0.05),
          fill: true,
          tension: 0.4,
          pointRadius: 0,
          pointHoverRadius: 6,
          borderWidth: 2,
        },
      ],
    }),
    [timeline, primaryColor, mutedColor]
  )

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        intersect: false,
        mode: 'index' as const,
      },
      plugins: {
        legend: {
          display: showLegend,
          position: 'top' as const,
          labels: {
            color: chartTheme.textColor,
            usePointStyle: true,
            padding: 20,
            font: {
              family: "'Source Sans 3', sans-serif",
            },
          },
        },
        tooltip: {
          backgroundColor: chartTheme.tooltipBg,
          titleColor: chartTheme.tooltipText,
          bodyColor: chartTheme.tooltipText,
          borderColor: chartTheme.gridColor,
          borderWidth: 1,
          padding: 12,
          callbacks: {
            label: (ctx: { dataset: { label?: string }; raw: unknown }) =>
              `${ctx.dataset.label}: ${formatCurrency(ctx.raw as number)}`,
          },
        },
      },
      scales: {
        x: {
          grid: {
            color: chartTheme.gridColor,
          },
          ticks: {
            color: chartTheme.textColor,
            font: {
              family: "'Source Sans 3', sans-serif",
            },
          },
        },
        y: {
          grid: {
            color: chartTheme.gridColor,
          },
          ticks: {
            color: chartTheme.textColor,
            font: {
              family: "'Source Sans 3', sans-serif",
            },
            callback: (value: string | number) => formatCurrency(value as number),
          },
        },
      },
    }),
    [chartTheme, showLegend]
  )

  return (
    <div style={{ height }}>
      <Line data={data} options={options} />
    </div>
  )
}
